import { jsPDF } from 'jspdf'
import backgroundImage from 'assets/images/pdf-background.png'
import Avatar from 'appAssets/appImages/Avatar.png'

interface PdfLink {
  text: string
  url: string
  startIndex: number
  endIndex: number
}

export const generateEnhancedPDF = async (
  filename: string,
  title: string,
  description: string,
  content: string
) => {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = pdf.internal.pageSize.width
  const pageHeight = pdf.internal.pageSize.height
  const margin = 20
  const headerHeight = 40
  const footerHeight = 30
  const contentWidth = pageWidth - margin * 2

  let currentPage = 1
  let yPosition = headerHeight + 10

  // Add header
  const addHeader = () => {
    pdf.addImage(backgroundImage, 'PNG', 0, 0, pageWidth, headerHeight)
    pdf.setFontSize(8)
    pdf.setTextColor(255, 255, 255)
    pdf.text('www.gotackle.app', 25, 25)

    const currentDate = new Date()
    const formattedDate = `Created: ${
      currentDate.getMonth() + 1
    }/${currentDate.getDate()}/${currentDate.getFullYear()}`
    pdf.text(formattedDate, 25, 30)
  }

  // Add footer
  const addFooter = (pageNum: number) => {
    const footerY = pageHeight - 20
    pdf.setDrawColor(229, 231, 235)
    pdf.line(20, footerY - 10, pageWidth - 20, footerY - 10)

    const avatarSize = 8
    pdf.addImage(Avatar, 'PNG', 20, footerY - 4, avatarSize, avatarSize)

    pdf.setFontSize(8)
    pdf.setTextColor(128, 128, 128)
    pdf.setFont('helvetica', 'normal')
    pdf.text('Powered by', 20 + avatarSize + 2, footerY - 1)

    pdf.setTextColor(33, 33, 33)
    pdf.setFont('helvetica', 'bold')
    pdf.setFontSize(9)
    pdf.text('Coach Logic', 20 + avatarSize + 2, footerY + 3)

    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(128, 128, 128)
    pdf.text(`Page ${pageNum}`, pageWidth - 30, footerY, { align: 'right' })
  }

  // Check if new page is needed
  const checkNewPage = (requiredHeight: number) => {
    if (yPosition + requiredHeight > pageHeight - footerHeight) {
      addFooter(currentPage)
      pdf.addPage()
      currentPage++
      addHeader()
      yPosition = headerHeight + 10
      return true
    }
    return false
  }

  // Add title
  addHeader()

  if (title) {
    pdf.setFontSize(16)
    pdf.setTextColor(25, 33, 61)
    pdf.setFont('helvetica', 'bold')
    const titleLines = pdf.splitTextToSize(title, contentWidth)
    checkNewPage(titleLines.length * 6)
    pdf.text(titleLines, margin, yPosition)
    yPosition += titleLines.length * 6 + 5
  }

  // Add description
  if (description) {
    pdf.setFontSize(14)
    pdf.setTextColor(63, 63, 70)
    pdf.setFont('helvetica', 'normal')
    const descLines = pdf.splitTextToSize(description, contentWidth)
    checkNewPage(descLines.length * 5)
    pdf.text(descLines, margin, yPosition)
    yPosition += descLines.length * 5 + 10
  }

  // Process content - extract links and format text
  const links: PdfLink[] = []
  let processedText = content

  // Extract markdown links
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g
  let match
  let offset = 0

  while ((match = linkRegex.exec(content)) !== null) {
    const linkText = match[1]
    const linkUrl = match[2]
    const matchStart = match.index - offset
    const matchEnd = matchStart + match[0].length

    // Replace the markdown link with just the text
    processedText =
      processedText.substring(0, matchStart) + linkText + processedText.substring(matchEnd)

    // Store link info
    links.push({
      text: linkText,
      url: linkUrl,
      startIndex: matchStart,
      endIndex: matchStart + linkText.length,
    })

    // Adjust offset for next iteration
    offset += match[0].length - linkText.length
  }

  // Split content into paragraphs and process
  const paragraphs = processedText.split(/\n\s*\n/).filter((p) => p.trim())

  pdf.setFontSize(12)
  pdf.setTextColor(71, 84, 103)
  pdf.setFont('helvetica', 'normal')

  paragraphs.forEach((paragraph) => {
    const trimmedParagraph = paragraph.trim()
    if (!trimmedParagraph) return

    // Check if it's a heading (### or ####)
    const headingMatch = trimmedParagraph.match(/^(#{3,4})\s*(.+)$/)
    if (headingMatch) {
      const level = headingMatch[1].length
      const headingText = headingMatch[2]

      yPosition += 8
      checkNewPage(10)

      // Style based on heading level
      if (level === 3) {
        pdf.setFontSize(14)
        pdf.setFont('helvetica', 'bold')
      } else {
        pdf.setFontSize(13)
        pdf.setFont('helvetica', 'bold')
      }

      const headingLines = pdf.splitTextToSize(headingText, contentWidth)
      pdf.text(headingLines, margin, yPosition)
      yPosition += headingLines.length * 6 + 5

      // Reset to normal text style
      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'normal')
      return
    }

    // Check if it's a numbered list item (both formats)
    const numberedMatch = trimmedParagraph.match(/^(\d+)\.\s*(.+)$/s)
    if (numberedMatch) {
      const number = numberedMatch[1]
      const text = numberedMatch[2]

      // Add some space before numbered items
      yPosition += 3
      checkNewPage(15)

      // Add the number in bold
      pdf.setFont('helvetica', 'bold')
      pdf.text(`${number}.`, margin, yPosition)

      // Add the text
      pdf.setFont('helvetica', 'normal')

      // Handle sub-bullets (lines starting with -)
      const lines = text.split('\n').filter((line) => line.trim())
      let currentY = yPosition

      lines.forEach((line, index) => {
        const trimmedLine = line.trim()
        if (trimmedLine.startsWith('- ')) {
          // Sub-bullet
          const bulletText = trimmedLine.substring(2)
          const bulletLines = pdf.splitTextToSize(`• ${bulletText}`, contentWidth - 25)

          if (index > 0) currentY += 3 // Add space between main text and bullets
          checkNewPage(bulletLines.length * 5)
          pdf.text(bulletLines, margin + 25, currentY)
          currentY += bulletLines.length * 5
        } else if (index === 0) {
          // Main text of the numbered item
          const textLines = pdf.splitTextToSize(trimmedLine, contentWidth - 15)
          checkNewPage(textLines.length * 5)
          pdf.text(textLines, margin + 15, currentY)
          currentY += textLines.length * 5
        } else {
          // Additional text lines
          const textLines = pdf.splitTextToSize(trimmedLine, contentWidth - 15)
          checkNewPage(textLines.length * 5)
          pdf.text(textLines, margin + 15, currentY)
          currentY += textLines.length * 5
        }
      })

      yPosition = currentY + 5
    } else {
      // Regular paragraph
      const lines = pdf.splitTextToSize(trimmedParagraph, contentWidth)
      checkNewPage(lines.length * 5)
      pdf.text(lines, margin, yPosition)
      yPosition += lines.length * 5 + 8
    }
  })

  // Add links section if there are any
  if (links.length > 0) {
    yPosition += 10
    checkNewPage(20)

    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(71, 84, 103)
    pdf.text('References:', margin, yPosition)
    yPosition += 10

    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(0, 0, 255)

    links.forEach((link, index) => {
      const linkText = `${index + 1}. ${link.text}`
      const lines = pdf.splitTextToSize(linkText, contentWidth)

      checkNewPage(lines.length * 5)
      pdf.text(lines, margin, yPosition)

      // Make it clickable
      const linkWidth = pdf.getTextWidth(linkText)
      pdf.link(margin, yPosition - 3, linkWidth, 5, { url: link.url })

      yPosition += lines.length * 5 + 3
    })
  }

  // Add final footer
  addFooter(currentPage)

  // Save the PDF
  pdf.save(filename)
}
